/**
 * Console Component
 * Terminal-like display for execution status and logs
 */
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const getLevelColor = (level) => {
  const l = (level || 'INFO').toUpperCase();
  if (l === 'ERROR' || l === 'CRITICAL') return 'text-red-400';
  if (l === 'WARNING' || l === 'WARN') return 'text-yellow-400';
  if (l === 'DEBUG') return 'text-cyan-400';
  return 'text-green-400'; // INFO and default
};

const getLevelBadgeClass = (level) => {
  const l = (level || 'INFO').toUpperCase();
  if (l === 'ERROR' || l === 'CRITICAL') return 'bg-red-900/50 text-red-300';
  if (l === 'WARNING' || l === 'WARN') return 'bg-yellow-900/40 text-yellow-200';
  if (l === 'DEBUG') return 'bg-cyan-900/40 text-cyan-200';
  return 'bg-gray-700 text-gray-300';
};

const StatusIndicator = ({ status }) => {
  const config = {
    idle: { color: 'bg-gray-400', label: 'Idle', icon: '○' },
    uploading: { color: 'bg-blue-500 animate-pulse', label: 'Uploading', icon: '↑' },
    processing: { color: 'bg-yellow-500 animate-pulse', label: 'Processing', icon: '⚙' },
    completed: { color: 'bg-green-500', label: 'Completed', icon: '✓' },
    error: { color: 'bg-red-500', label: 'Error', icon: '✕' }
  };

  const currentConfig = config[status] || config.idle;

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${currentConfig.color}`} />
      <span className="text-sm font-medium text-gray-700">{currentConfig.label}</span>
    </div>
  );
};

/**
 * Per-entry slide-in animation is decorative: it helps the user notice the first
 * few log lines, then becomes pure overhead once the console is chatty. A live
 * agent run can burst 50+ records/sec; at that volume, keeping ``AnimatePresence``
 * + ``motion.div`` for every row produces visibly stuttery scrolls and wasted
 * reflows without adding meaning. Above this threshold we render static rows.
 */
const ANIMATE_LOG_THRESHOLD = 20;

export default function Console({ status, logs, headerActions }) {
  const logsEndRef = useRef(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const animateRows = logs.length <= ANIMATE_LOG_THRESHOLD;

  return (
    <div className="h-full flex flex-col bg-gray-900 text-green-400 font-mono text-sm">
      {/* Console Header */}
      <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center justify-between gap-3">
        <StatusIndicator status={status} />
        <div className="flex items-center gap-2 min-w-0">
          {headerActions}
          <div className="text-xs text-gray-500 whitespace-nowrap">
            {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>
      </div>

      {/* Console Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {logs.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-600">
              <p className="mb-2">Console is ready</p>
              <p className="text-xs text-gray-500">Logs will appear here during execution</p>
            </div>
          </div>
        ) : (
          animateRows ? (
            <AnimatePresence initial={false}>
              {logs.map((log, index) => (
                <motion.div
                  key={log.id != null ? log.id : `${log.timestamp}-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-2"
                >
                  <span className="text-gray-500 text-xs">
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>{' '}
                  <span className={`text-[10px] uppercase px-1 py-0.5 rounded ${getLevelBadgeClass(log.level)}`}>
                    {log.level || 'INFO'}
                  </span>
                  {' '}
                  <span className={getLevelColor(log.level)}>{log.message}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            logs.map((log, index) => (
              <div
                key={log.id != null ? log.id : `${log.timestamp}-${index}`}
                className="mb-2"
              >
                <span className="text-gray-500 text-xs">
                  [{new Date(log.timestamp).toLocaleTimeString()}]
                </span>{' '}
                <span className={`text-[10px] uppercase px-1 py-0.5 rounded ${getLevelBadgeClass(log.level)}`}>
                  {log.level || 'INFO'}
                </span>
                {' '}
                <span className={getLevelColor(log.level)}>{log.message}</span>
              </div>
            ))
          )
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
}
